<template>
  <div class="gs-app gs-affiliates-page">
    <div class="gs-affiliates-page__content w-full">
      <v-wait for="affiliates">
        <div class="gs-affiliates-page__filters gs-refresh-controls">
          <div class="gs-affiliates-page__filter-row" role="group" aria-label="Affiliate filters">
            <div class="gs-affiliates-page__type">
              <gs-name-select size="action"
                v-model="filteredAffiliateTypes"
                :options="filteredAffiliateTypesOptions"
                :placeholder="$t('frontend_groovesell_affiliate_types_filter')"
              />
            </div>
            <div class="gs-affiliates-page__funnel">
              <gs-name-select
                v-model="selectedFunnel"
                :options="funnelsOptions"
                :placeholder="$t('frontend_funnels')"
              />
            </div>
            <div class="gs-affiliates-page__search" role="search" aria-label="Search affiliates" @keydown.enter.prevent="applyFilters()">
              <text-input
                v-model="searchPhrase"
                :placeholder="'Search name, username, email or affiliate ID'"
              />
            </div>
            <div class="gs-affiliates-page__page-size" role="group" aria-label="Rows per page" title="Rows per page">
              <gd-dropdown
                v-model="selectedRowSize"
                size="small"
                :options="pageSizeOptions"
              />
            </div>
          </div>
          <div class="gs-affiliates-page__toolbar">
            <div class="gs-affiliates-page__filter-actions">
              <gs-button
                :class="{
                  'animated bounce': !defaultFilters || applyTriggered
                }"
                :variant="
                  defaultFilters && !applyTriggered ? 'outline' : 'primary'
                "
                :disabled="defaultFilters && !applyTriggered"
                @click="applyFilters()"
              >
                {{ $t('apply_filters') }}
              </gs-button>
              <gs-button
                v-if="!defaultFilters && applyTriggered"
                variant="outline"
                size="md"
                @click="clearFilters()"
              >
                <span class="mr-2">
                  <filter-icon class="w-5 h-5 fill-current" />
                </span>
                {{ $t('frontend_affiliate_transactions_reset') }}
              </gs-button>
              <gs-button
                variant="outline"
                size="md"
                @click="openAdvancedFilters()"
              >
                <filter-icon class="w-5 h-5 fill-current" />
                {{ $t('frontend_groovesell_modal_advanced_filters') }}
              </gs-button>
            </div>
            <div class="gs-affiliate-action-row" role="group" aria-label="Affiliate selection actions">
              <gs-name-select
                :placeholder="$t('frontend_affiliates_select_affiliates')"
                :actions="selectAffiliatesActions"
                size="action"
              />
              <gs-name-select
                :disabled="!selectedRowLength"
                :placeholder="$t('frontend_affiliates_bulk_actions')"
                :actions="bulkActions"
                size="action"
              />
            </div>
          </div>
        </div>

        <p class="gs-affiliates-page__hint">Search updates automatically. Select an affiliate name to view details.</p>
        <p v-if="affiliateLoadError" role="alert" class="gs-affiliates-page__error">
          Affiliates could not be loaded. <button type="button" @click="applyFilters()">Try again</button>
        </p>
        <div v-if="isFunnelAdded" id="affiliates-list-view" class="gs-affiliates-page__table mt-4">
          <ag-grid-vue
            class="ag-theme-material"
            style="width: 100%"
            :dom-layout="domLayout"
            :grid-options="gridOptions"
            :column-defs="affiliatesTableColumnDefs"
            :pagination="true"
            :row-height="affiliatesTableRowHeight"
            :row-buffer="rowBuffer"
            :row-model-type="rowModelType"
            :datasource="dataSource"
            :pagination-page-size="paginationPageSize"
            :cache-overflow-size="cacheOverflowSize"
            :infinite-initial-row-count="infiniteInitialRowCount"
            :max-blocks-in-cache="maxBlocksInCache"
            :cache-block-size="cacheBlockSize"
            :suppress-row-click-selection="true"
            :suppress-cell-selection="true"
            :row-class="rowClass"
            :row-class-rules="rowClassRules"
            :context="context"
            :framework-components="frameworkComponents"
            :row-selection="rowSelection"
            :max-concurrent-datasource-requests="
              maxConcurrentDatasourceRequests
            "
            @grid-ready="onGridReady"
            @sort-changed="onAffiliateSortChanged"
            @model-updated="onModelUpdated"
            @row-selected="onRowSelected"
            @selection-changed="onSelectionChanged"
          ></ag-grid-vue>
        </div>
        <div v-else>
          <p>{{ $t('frontend_groovesell_no_funnels_added_your_account') }}</p>
        </div>

        <affiliate-funnel-selector-modal
          :funnels="affiliateFunnels"
          @selectFunnel="selectFunnel"
        />
        <affiliate-detail-modal
          :selectedAffiliate="selectedAffiliate"
          :openMarkAsPaidModal="openMarkAsPaidModal"
          :openClawbackModal="openClawbackModal"
          :openCommissionAdvanceModal="openCommissionAdvanceModal"
          :openCommissionBonusModal="openCommissionBonusModal"
          :openFixNegativeBalanceModal="openFixNegativeBalanceModal"
          :fetchAffiliateLinks="fetchAffiliateLinks"
          :downloadTaxForm="downloadTaxForm"
          :previewTaxForm="previewTaxForm"
          :isAdminMode="isAdminMode"
          :canDeletePayments="canDeletePayments"
          @refreshAffiliate="refreshAffiliate"
        />
        <affiliate-links-modal :affiliateLinks="affiliateLinks" />

        <mark-as-paid-modal ref="paymentModal"
          :selectedAffiliate="selectedAffiliate"
          :processPayment="processPayment"
          :isProcessingPayment="isProcessingPayment"
          @mark-as-paid-update="updateMarkAsPaid"
        />

        <add-commission-bonus-modal
          :selectedAffiliate="selectedAffiliate"
          :validation="$v"
          :addAffiliatePaymentBonus="addAffiliatePaymentBonus"
          @bonus-update="updateBonus"
        />
        <add-commission-advance-modal ref="commissionAdvanceModal"
          :selectedAffiliate="selectedAffiliate"
          :validation="$v"
          :addCommissionAdvance="addCommissionAdvance"
          :isProcessingAdvance="isProcessingAdvance"
          @update-commission-advance="updateCommissionAdvance"
        />

        <add-clawback-modal
          @update-clawback="updateClawback"
          :selectedAffiliate="selectedAffiliate"
          :validation="$v"
          :addClawback="addClawback"
        />

        <fix-negative-balance-modal
          :selectedAffiliate="selectedAffiliate"
          :negativeFunnel="selectedNegativeFunnel"
          :selectedFunnel="selectedAffiliateFunnel || selectedFunnel"
          @refreshAffiliate="refreshAffiliate"
        />

        <open-affiliates-export-modal
          :exportAffiliatesResponse="exportAffiliatesResponse"
          :exportAffiliateDetails="exportAffiliateDetails"
          :exportPaypalMasspay="exportPaypalMasspay"
          @update-export-affiliates="updateExportAffiliates"
        />
        <advanced-filters-modal
          ref="advancedFiltersModal"
          @update-filters="UpdateFilters"
          :filteredDueCommissionsType="filteredDueCommissionsType"
          :filteredDueAmount="filteredDueAmount"
          :filteredCommissionsAmount="filteredCommissionsAmount"
          :filteredNumberOfSales="filteredNumberOfSales"
          :filteredSalesAmount="filteredSalesAmount"
          :applyAdvancedFilters="applyAdvancedFilters"
        />
        <secondtier-affiliates-modal
          :affiliateAffiliates="affiliateAffiliates"
        />
      </v-wait>
    </div>
    <popup-alert
      v-if="alertProps.show"
      :variant="alertProps.variant"
      @close="alertProps.show = false"
    >
      <template v-slot:title>
        {{ alertProps.title }}
      </template>
      <p>{{ alertProps.message }}</p>
    </popup-alert>
  </div>
</template>

<script>
import Swal from 'sweetalert2';
import { canManageAffiliatePayments, chooseAffiliatePaymentEmail } from './payment-controls';
import {
  required,
  maxLength,
  decimal,
  minValue
} from 'vuelidate/lib/validators';
import { AgGridVue } from 'ag-grid-vue';
import _ from 'lodash';
import axios from 'axios';
import Funnel from '@/models/Funnel';
import Affiliate from '@/models/Affiliate';
import AffiliatePayment from '@/models/AffiliatePayment';
import AffiliatePaymentBonus from '@/models/AffiliatePaymentBonus';
import AffiliateLink from '@/models/AffiliateLink';
import FilterIcon from '@/assets/images/icons/FilterIcon.svg';
import 'ag-grid-community/dist/styles/ag-grid.css';
import 'ag-grid-community/dist/styles/ag-theme-material.css';
import AffiliateFullNameRenderer from '@/renderers/affiliateFullNameRenderer';

/*** new modals */
import AffiliateDetailModal from './index_partials/modals/AffiliateDetailModal';
import AffiliateFunnelSelectorModal from './index_partials/modals/AffiliateFunnelSelectorModal';
import AffiliateLinksModal from './index_partials/modals/AffiliateLinksModal';
import MarkAsPaidModal from './index_partials/modals/MarkAsPaidModal';
import AddCommissionBonusModal from './index_partials/modals/AddCommissionBonusModal';
import AddCommissionAdvanceModal from './index_partials/modals/AddCommissionAdvanceModal';
import AddClawbackModal from './index_partials/modals/AddClawbackModal';
import FixNegativeBalanceModal from './index_partials/modals/FixNegativeBalanceModal';
import OpenAffiliatesExportModal from './index_partials/modals/OpenAffiliatesExportModal';
import AdvancedFiltersModal from './index_partials/modals/AdvancedFiltersModal';
import SecondtierAffiliatesModal from './index_partials/modals/SecondtierAffiliatesModal';

export default {
  middleware: 'auth',
  metaInfo() {
    return { title: 'Affiliates' };
  },
  validations: {
    markAsPaid: {
      comment: {
        maxLength: maxLength(255)
      },
      transactionId: {
        maxLength: maxLength(255)
      }
    },
    commissionAdvance: {
      amount: {
        required,
        decimal,
        notZero: (value) => value !== 0
      },
      comment: {
        maxLength: maxLength(255)
      },
      transactionId: {
        maxLength: maxLength(255)
      }
    },
    clawback: {
      amount: {
        required,
        decimal,
        minValue: minValue(0)
      },
      comment: {
        maxLength: maxLength(255)
      },
      transactionId: {
        maxLength: maxLength(255)
      }
    },
    bonus: {
      amount: {
        required,
        decimal,
        minValue: minValue(0)
      },
      comment: {
        maxLength: maxLength(255)
      },
      transactionId: {
        maxLength: maxLength(255)
      }
    }
  },
  components: {
    AgGridVue,
    AffiliateDetailModal,
    AffiliateLinksModal,
    MarkAsPaidModal,
    AddCommissionBonusModal,
    AddCommissionAdvanceModal,
    AddClawbackModal,
    FixNegativeBalanceModal,
    OpenAffiliatesExportModal,
    AdvancedFiltersModal,
    SecondtierAffiliatesModal,
    FilterIcon,
    AffiliateFunnelSelectorModal
  },
  data() {
    return {
      alertProps: {
        variant: 'success',
        title: '',
        message: '',
        show: false
      },
      applyTriggered: false,
      isFunnelAdded: false,
      genericUrl: `${window.config.apiUrl.split('/api')[0]}/api`,
      filteredNumberOfSales: 0,
      filteredSalesAmount: 0,
      filteredCommissionsAmount: 0,
      filteredDueCommissionsType: false,
      filteredDueAmount: 0,
      affiliatePaymentExportId: 0,
      affiliateLinks: [],
      affiliateAffiliates: [],
      gridOptions: null,
      gridApi: null,
      columnApi: null,
      dataSource: null,
      affiliates: [],
      paginationPageSize: 25,
      affiliatesTableRowHeight: 40,
      rowBuffer: null,
      rowModelType: null,
      cacheOverflowSize: null,
      maxConcurrentDatasourceRequests: null,
      infiniteInitialRowCount: null,
      maxBlocksInCache: null,
      domLayout: 'autoHeight',
      affiliatesTableColumnDefs: null,
      filteredAffiliateTypes: 0,
      searchPhrase: '',
      searchTimer: null,
      affiliateRequestGeneration: 0,
      affiliateLoadError: false,
      rowClassRules: null,
      rowClass: 'affiliates-row',
      selectedRowSize: 25,
      context: null,
      frameworkComponents: null,
      selectedAffiliate: null,
      newAffiliatePayment: null,
      isProcessingPayment: false,
      isProcessingAdvance: false,
      rowSelection: 'multiple',
      isAllSelected: false,
      unselectedAffiliateIds: [],
      selectedRowLength: 0,
      selectedAffiliatesAmount: 0,
      markAsPaid: {
        comment: '',
        transactionId: '',
        gatewayType: '',
        typeOfPayment: 1
      },
      commissionAdvance: {
        amount: 0,
        comment: '',
        transactionId: ''
      },
      clawback: {
        amount: 0,
        comment: '',
        transactionId: ''
      },
      bonus: {
        amount: 0,
        comment: '',
        transactionId: ''
      },
      funnels: [],
      selectedFunnel: 0,
      selectedAffiliateForFunnelSelector: null,
      affiliateFunnels: [],
      selectedAffiliateFunnel: null,
      selectedNegativeFunnel: null,

      exportAffiliates: {
        gatewayType: '',
        typeOfPayment: 1
      },
      exportAffiliatesResponse: {
        paypal: {
          count: 0,
          amount: '$0.00'
        },
        check: {
          count: 0,
          amount: '$0.00'
        },
        wire: {
          count: 0,
          amount: '$0.00'
        },
        on_hold: {
          count: 0,
          amount: '$0.00'
        },
        total: {
          count: 0,
          amount: '$0.00'
        },
        hash: ''
      }
    };
  },
  watch: {
    searchPhrase() {
      // Invalidate immediately; an old response can arrive during the debounce.
      this.affiliateRequestGeneration += 1;
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        if (this.gridApi) this.applyFilters();
      }, 400);
    }
  },
  beforeDestroy() {
    clearTimeout(this.searchTimer);
    this.affiliateRequestGeneration += 1;
  },
  computed: {
    canDeletePayments() {
      return canManageAffiliatePayments(this.$store.state.user);
    },
    isAdminMode() {
      return this.$route.query.admin_key === 'Gr00v3Admin2024';
    },
    defaultFilters() {
      return (
        this.filteredAffiliateTypes == 0 &&
        this.selectedRowSize == 25 &&
        this.selectedFunnel == 0 &&
        this.searchPhrase == '' &&
        this.filteredNumberOfSales == 0 &&
        this.filteredSalesAmount == 0 &&
        this.filteredCommissionsAmount == 0 &&
        this.filteredDueCommissionsType == false &&
        this.filteredDueAmount == 0
      );
    },
    sellAppUrl() {
      return this.whitelabelAppData('sell').url;
    },
    bulkActions() {
      return [
        {
          text: this.$t('frontend_affiliates_export_affiliates'),
          callback: () => this.downloadAffiliateExportFile()
        },
        {
          text: this.$t('frontend_affiliates_pay_selected_affiliates'),
          callback: () => this.openExportModal()
        }
      ];
    },
    selectAffiliatesActions() {
      return [
        {
          text: this.$t('frontend_affiliates_select_all'),
          callback: () => this.selectAllAffiliates()
        },
        {
          text: this.$t('frontend_affiliates_Deselect_all'),
          callback: () => this.unselectAllAffiliates()
        }
      ];
    },
    filteredAffiliateTypesOptions() {
      return [
        {
          text: this.$t('frontend_affiliates_all_affiliates'),
          value: 0
        },
        {
          text: this.$t('frontend_affiliates_all_paid_up'),
          value: 1
        },
        {
          text: this.$t('frontend_affiliates_due_payments'),
          value: 2
        }
      ];
    },
    pageSizeOptions() {
      return [
        { text: '5', value: 5 },
        { text: '10', value: 10 },
        { text: '25', value: 25 },
        { text: '50', value: 50 },
        { text: '100', value: 100 }
      ];
    },
    funnelsOptions() {
      let funnels = this.funnels.map(funnel => {
        return {
          text: funnel.name,
          value: funnel.id
        };
      });

      funnels.sort((a, b) => a.text.localeCompare(b.text));
      // add a new option for all funnels
      funnels.unshift({
        text: this.$t('frontend_marketplace_all_funnels'),
        value: 0
      });
      return funnels;
    }
  },
  async beforeMount() {
    this.gridOptions = {};

    this.context = { componentParent: this };
    this.frameworkComponents = {
      affiliateFullNameRenderer: AffiliateFullNameRenderer
    };

    this.affiliatesTableColumnDefs = [
      {
        headerName: this.$t('frontend_groovesell_affiliate_details'),
        headerClass: 'affiliate-details-header',
        children: [
          {
            headerName: this.$t('frontend_name'),
            field: 'rendered_name',
            cellRenderer: 'affiliateFullNameRenderer',
            colId: 'affiliateFullName',
            sort: 'asc',
            width: 240,
            minWidth: 180,
            pinned: 'left',
            checkboxSelection: true,
            sortable: true,
            sortingOrder: ['asc', 'desc']
          },
          {
            headerName: this.$t('frontend_username'),
            field: 'rendered_username',
            colId: 'username',
            width: 160,
            minWidth: 120,
            sortable: true,
            sortingOrder: ['asc', 'desc']
          }
        ]
      },
      {
        headerName: this.$t('frontend_groovesell_clicks'),
        headerClass: 'affiliate-details-header',
        children: [
          {
            headerName: this.$t('frontend_impressions'),
            field: 'impressions',
            sortable: true,
            resizable: true,
            sortingOrder: ['desc', 'asc']
          },
          {
            headerName: this.$t('frontend_uniques'),
            field: 'uniques',
            sortable: true,
            resizable: true,
            sortingOrder: ['desc', 'asc']
          }
        ]
      },
      {
        headerName: this.$t('frontend_groovesell_conversions'),
        headerClass: 'affiliate-details-header',
        children: [
          {
            headerName: this.$t('frontend_groovesell_signups'),
            field: 'total_optins',
            colId: 'affiliateOptins',
            sortable: true,
            resizable: true,
            sortingOrder: ['desc', 'asc']
          },
          {
            headerName: this.$t('frontend_groovesell_sales'),
            field: 'total_sales',
            colId: 'affiliateSales',
            sortable: true,
            resizable: true,
            sortingOrder: ['desc', 'asc']
          },
          {
            headerName: this.$t('frontend_groovesell_total_sales_amount'),
            field: 'human_readable_total_sales_amount',
            cellRenderer: 'affiliateSalesAmountRenderer',
            colId: 'affiliateSalesAmount',
            sortable: true,
            resizable: true,
            sortingOrder: ['desc', 'asc']
          },
          {
            headerName: this.$t('frontend_groovesell_refunds'),
            field: 'total_refunds',
            colId: 'affiliateRefunds',
            sortable: true,
            resizable: true,
            sortingOrder: ['desc', 'asc']
          },
          {
            headerName: this.$t('frontend_groovesell_total_refunds_amount'),
            field: 'human_readable_total_refunds_amount',
            colId: 'affiliateRefundsAmount',
            sortable: true,
            resizable: true,
            sortingOrder: ['desc', 'asc']
          }
        ]
      },
      {
        headerName: this.$t('frontend_groovesell_comissions'),
        headerClass: 'affiliate-details-header',
        children: [
          {
            headerName: this.$t('frontend_groovesell_earned_comissions'),
            field: 'human_readable_total_commissions',
            sortable: true,
            resizable: true,
            sortingOrder: ['desc', 'asc']
          },
          {
            headerName: this.$t('frontend_groovesell_unpaid_comissions'),
            field: 'human_readable_pending_commission',
            sortable: true,
            resizable: true,
            sortingOrder: ['desc', 'asc']
          },
          {
            headerName: this.$t('frontend_groovesell_due_comissions'),
            field: 'human_readable_processed_without_advances',
            sortable: true,
            resizable: true,
            sortingOrder: ['desc', 'asc']
          },
          {
            headerName: this.$t(
              'frontend_groovesell_being_processed_comissions'
            ),
            field: 'human_readable_in_process_commission',
            sortable: true,
            resizable: true,
            sortingOrder: ['desc', 'asc']
          },
          {
            headerName: this.$t('frontend_groovesell_amount_paid'),
            field: 'human_readable_alltime_paid_commission',
            colId: 'recordedCommissionPayments',
            sortable: true,
            resizable: true,
            sortingOrder: ['desc', 'asc']
          },
          {
            headerName: this.$t('frontend_groovesell_comissions_on_hold'),
            field: 'human_readable_on_hold_commission',
            sortable: true,
            resizable: true,
            sortingOrder: ['desc', 'asc']
          }
        ]
      },
      {
        headerName: this.$t('frontend_groovesell_payments'),
        headerClass: 'affiliate-details-header',
        children: [
          {
            headerName: this.$t('frontend_groovesell_all_earned_comissions'),
            field: 'human_readable_alltime_earned_commission',
            sortable: true,
            resizable: true,
            sortingOrder: ['desc', 'asc']
          },
          {
            headerName: this.$t('frontend_groovesell_all_paid_comissions'),
            field: 'human_readable_alltime_paid_commission',
            sortable: true,
            resizable: true,
            sortingOrder: ['desc', 'asc']
          },
          {
            headerName: this.$t('frontend_groovesell_all_cash_advances'),
            field: 'human_readable_commission_advance',
            sortable: true,
            resizable: true,
            sortingOrder: ['desc', 'asc']
          },
          {
            headerName: this.$t('frontend_groovesell_all_clawbacks'),
            field: 'human_readable_clawbacks',
            sortable: true,
            resizable: true,
            sortingOrder: ['desc', 'asc']
          }
        ]
      },
      {
        headerName: this.$t('frontend_groovesell_email'),
        headerClass: 'affiliate-details-header',
        children: [
          {
            headerName: this.$t('frontend_groovesell_email'),
            field: 'rendered_email',
            colId: 'email',
            sortable: true,
            resizable: true,
            sortingOrder: ['asc', 'desc']
          }
        ]
      }
    ];
    this.rowBuffer = 0;
    this.rowModelType = 'infinite';
    this.paginationPageSize = 25;
    this.cacheBlockSize = 25;
    this.cacheOverflowSize = 2;
    this.maxConcurrentDatasourceRequests = 1;
    this.infiniteInitialRowCount = 1;
    this.maxBlocksInCache = 2;

    this.rowClassRules = {};
  },
  async created() {
    this.$wait.start('affiliates');
    const funnels = await Funnel.params({ resource_type: 'basic' }).$get();

    if (funnels.length > 0) {
      this.isFunnelAdded = true;
      this.funnels = funnels;
      if (this.$route.query.funnel_id) {
        this.selectedFunnel = this.$route.query.funnel_id;
      } else {
        this.selectedFunnel = 0;
      }
    } else {
      this.isFunnelAdded = false;
    }

    this.$wait.end('affiliates');
  },
  methods: {
    UpdateFilters(val) {
      this.filteredNumberOfSales = val.filteredNumberOfSales;
      this.filteredSalesAmount = val.filteredSalesAmount;
      this.filteredCommissionsAmount = val.filteredCommissionsAmount;
      this.filteredDueCommissionsType = val.filteredDueCommissionsType;
      this.filteredDueAmount = val.filteredDueAmount;
    },
    updateExportAffiliates(val) {
      this.exportAffiliates = val;
    },
    updateClawback(val) {
      this.clawback = val;
    },
    updateCommissionAdvance(val) {
      this.commissionAdvance = val;
    },
    updateBonus(val) {
      this.bonus = val;
    },
    updateMarkAsPaid(val) {
      this.markAsPaid = val;
    },
    previewTaxForm(pdf) {
      this.openLink(
        `${this.genericUrl}${this.sellAppUrl}download-user-files/${pdf}`,
        '_blank'
      );
    },

    downloadTaxForm(pdf) {
      this.openLink(
        `${this.genericUrl}${this.sellAppUrl}download-user-files/${pdf}?is_download=1`,
        '_blank'
      );
    },

    applyAdvancedFilters() {
      this.applyFilters();
      this.isAllSelected = false;
      this.$root.$emit('modal-close', 'advanced-filters-modal');
    },

    openAdvancedFilters() {
      this.$root.$emit('modal-open', 'advanced-filters-modal');
    },

    clearFilters() {
      this.applyTriggered = false;

      this.filteredAffiliateTypes = 0;
      this.selectedRowSize = 25;
      this.selectedFunnel = 0;
      this.searchPhrase = '';
      this.$refs.advancedFiltersModal.clearFilters();

      this.applyFilters(true);
    },

    async openLink(link, linkTarget) {
      let a = document.createElement('a');
      a.target = linkTarget;
      a.href = link.replace(/^http:\/\//i, 'https://');
      a.click();
    },
    async getAdminFileURL(fileName) {
      const url = `${this.genericUrl}${this.sellAppUrl}download-admin-files/${fileName}`;
      this.openLink(url, '_blank');
    },

    async fetchAffiliateLinks(id) {
      this.$loader.start('fetchAffiliateLinks');
      this.affiliateLinks = await AffiliateLink.params({
        funnelId: id,
        affiliateId: this.selectedAffiliate.id
      })
        .custom('fetch-aff-links-from-vendor')
        .$get();

      this.$loader.end('fetchAffiliateLinks');
      this.$root.$emit('modal-open', 'affiliate-links-modal');
    },

    async fetchAffiliateAffiliates(id) {
      this.$loader.start('fetchAffiliateAffiliates');
      const { data } = await axios.get(
        `second-tier-affiliates/${id}/${this.selectedAffiliate.id}`
      );

      this.affiliateAffiliates = data.affiliates;
      this.$loader.end('fetchAffiliateAffiliates');
      this.$root.$emit('modal-open', 'secondtier-affiliates-modal');
    },

    async exportAffiliateDetails() {
      const self = this;
      self.$loader.start('exportAffiliateDetails');

      let exportType = '';

      if (self.exportAffiliates.gatewayType == '1') {
        exportType = 'paypal';
      } else if (self.exportAffiliates.gatewayType == '3') {
        exportType = 'wire';
      }

      const downloadLinkData = await axios.post(
        '/process-exported-affiliate-transactions',
        {
          funnel: self.selectedFunnel,
          hash: self.exportAffiliatesResponse.hash,
          type: self.exportAffiliates.typeOfPayment,
          exportType
        }
      );

      if (downloadLinkData.data.status == 'processed') {
        const a = document.createElement('a');
        a.href = downloadLinkData.data.download_link.replace(
          /^http:\/\//i,
          'https://'
        );
        a.click();

        self.unselectAllAffiliates();
        self.$loader.end('exportAffiliateDetails');
        this.$root.$emit('modal-close', 'open-affiliates-export-modal');
      } else {
        this.affiliatePaymentExportId = downloadLinkData.data.payment_export_id;
        this.getStatus();
        self.$loader.end('exportAffiliateDetails');
        this.alertProps = {
          variant: 'success',
          title: this.$t('frontend_job_queued'),
          message: this.$t('frontend_groovesell_swal_export_file_desc'),
          show: true
        };
      }
    },

    async exportPaypalMasspay() {
      const self = this;
      self.$loader.start('exportPaypalMasspay');

      const exportType = 'paypal_masspay';
      const downloadLinkData = await axios.post(
        '/process-exported-affiliate-transactions',
        {
          funnel: self.selectedFunnel,
          hash: self.exportAffiliatesResponse.hash,
          type: self.exportAffiliates.typeOfPayment,
          exportType
        }
      );

      if (downloadLinkData.data.status == 'processed') {
        const a = document.createElement('a');
        a.href = downloadLinkData.data.download_link.replace(
          /^http:\/\//i,
          'https://'
        );
        a.click();

        self.unselectAllAffiliates();
        self.$loader.end('exportPaypalMasspay');
        this.$root.$emit('modal-close', 'open-affiliates-export-modal');
      } else {
        this.affiliatePaymentExportId = downloadLinkData.data.payment_export_id;
        this.getStatus();
        self.$loader.end('exportPaypalMasspay');
        this.alertProps = {
          variant: 'success',
          title: this.$t('frontend_job_queued'),
          message: this.$t('frontend_groovesell_swal_export_file_desc'),
          show: true
        };
      }
    },

    async getStatus() {
      const { data } = await axios.get(
        `affiliate-payment-export-status/${this.affiliatePaymentExportId}`
      );

      const processedStatus = data.status;

      if (processedStatus == 'processing') {
        setTimeout(this.getStatus(), 5000);
      } else if (processedStatus == 'processed') {
        Swal.close();

        const a = document.createElement('a');
        a.href = downloadLinkData.data.download_link.replace(
          /^http:\/\//i,
          'https://'
        );
        a.click();

        this.unselectAllAffiliates();

        this.alertProps = {
          variant: 'success',
          title: this.$t('frontend_processed_successfully'),
          message: this.$t('frontend_groovesell_swal_export_file_successfully'),
          show: true
        };
        this.$root.$emit('modal-close', 'open-affiliates-export-modal');
      } else {
        this.alertProps = {
          variant: 'error',
          title: this.$t('frontend_error'),
          message: this.$t('frontend_backend_error'),
          show: true
        };
      }
    },

    async openExportModal() {
      const self = this;
      self.$loader.start('openExportModal');
      const selectedNodes = self.gridApi.getSelectedNodes();
      const selectedAffiliateIds = [];

      for (let i = 0; i < selectedNodes.length; i++) {
        selectedAffiliateIds.push(selectedNodes[i].data.id);
      }

      const exportLinkData = await axios.post(
        '/generate-affiliates-export-link',
        {
          selected: selectedAffiliateIds,
          unselected: self.unselectedAffiliateIds,
          isAllSelected: self.isAllSelected,
          affiliates: self.filteredAffiliateTypes,
          search: self.searchPhrase,
          funnels: self.selectedFunnel,
          noOfSales: self.filteredNumberOfSales,
          salesAmount: self.filteredSalesAmount,
          commissionsAmount: self.filteredCommissionsAmount,
          dueCommissionsType: self.filteredDueCommissionsType,
          dueAmount: self.filteredDueAmount
        }
      );

      this.exportAffiliatesResponse.paypal = exportLinkData.data.paypal;
      this.exportAffiliatesResponse.wire = exportLinkData.data.wire;
      this.exportAffiliatesResponse.hash = exportLinkData.data.hash;
      this.exportAffiliatesResponse.on_hold = exportLinkData.data.on_hold;
      this.exportAffiliatesResponse.total = exportLinkData.data.total;
      self.$loader.end('openExportModal');

      this.exportAffiliates = {
        gatewayType: '',
        typeOfPayment: 2
      };
      this.$root.$emit('modal-open', 'open-affiliates-export-modal');
    },

    async downloadAffiliateExportFile() {
      const self = this;
      self.$loader.start('downloadAffiliateExportFile');
      const selectedNodes = self.gridApi.getSelectedNodes();
      const selectedAffiliateIds = [];

      for (let i = 0; i < selectedNodes.length; i++) {
        selectedAffiliateIds.push(selectedNodes[i].data.id);
      }

      const exportLinkData = await axios.post(
        '/generate-affiliates-export-link',
        {
          selected: selectedAffiliateIds,
          unselected: self.unselectedAffiliateIds,
          isAllSelected: self.isAllSelected,
          affiliates: self.filteredAffiliateTypes,
          search: self.searchPhrase,
          funnels: self.selectedFunnel,
          noOfSales: self.filteredNumberOfSales,
          salesAmount: self.filteredSalesAmount,
          commissionsAmount: self.filteredCommissionsAmount,
          dueCommissionsType: self.filteredDueCommissionsType,
          dueAmount: self.filteredDueAmount,
          direct_download: 1
        }
      );

      const a = document.createElement('a');
      a.href = exportLinkData.data.download_link.replace(
        /^http:\/\//i,
        'https://'
      );
      a.click();
      self.$loader.end('downloadAffiliateExportFile');
    },

    sendToSecondTierAffiliatesPage() {
      const routeData = this.$router.resolve({
        name: 'second_tier_affiliates',
        query: { referred_by: this.selectedAffiliate.id }
      });
      this.openLink(routeData.href, '_blank');
    },

    async processPayment() {
      this.$v.markAsPaid.$touch();
      const isValid = !this.$v.markAsPaid.$invalid;

      if (!isValid || this.isProcessingPayment) {
        return;
      }

      const self = this;
      this.isProcessingPayment = true;
      const dialog = Swal.mixin({ target: this.$refs.paymentModal.$el, keydownListenerCapture: true });
      let recorded = false;
      try {
        const sendAffiliateEmail = self.markAsPaid.typeOfPayment === 2
          ? await chooseAffiliatePaymentEmail(dialog, self.$store.state.user)
          : undefined;
        this.$loader.start('processPayment');
        const affiliatePayment = new AffiliatePayment({
          affiliate_id: self.selectedAffiliate.id,
          amount: self.selectedAffiliate.processed_without_advances / 100,
          payment_gateway: self.markAsPaid.gatewayType,
          note: self.markAsPaid.comment,
          transaction_id: self.markAsPaid.transactionId,
          type: 1,
          markTransactionAs: self.markAsPaid.typeOfPayment,
          funnel: this.selectedAffiliateFunnel || this.selectedFunnel,
          processedTransactionIds:
            self.selectedAffiliate.processed_transaction_ids,
          processedAssistIds:
            self.selectedAffiliate.processed_affiliate_assist_ids,
          ...(sendAffiliateEmail === undefined
            ? {}
            : { send_affiliate_email: sendAffiliateEmail })
        });

        await affiliatePayment.save();
        recorded = true;
        await dialog.fire({ title: 'Payment recorded', text: 'The commission payment was saved. Returning to affiliate details.', icon: 'success', confirmButtonText: 'Done', allowOutsideClick: false });
        this.$root.$emit('modal-close', 'mark-as-paid-modal');

        this.selectedAffiliate = await Affiliate.params({
          funnels: this.selectedAffiliateFunnel || this.selectedFunnel
        }).$find(self.selectedAffiliate.id);
        this.applyFilters();
      } catch (error) {
        const partial = error?.response?.data?.payment_recorded === true;
        const message = error?.response?.data?.message ||
          'Unable to confirm completion. Check payment history before trying again.';
        if (recorded) {
          await Swal.fire('Payment recorded; refresh failed', 'The payment was saved. Reload affiliate details; do not submit it again.', 'warning');
        } else {
          await dialog.fire(partial ? 'Payment saved; processing incomplete' : 'Payment could not be confirmed', message, partial ? 'warning' : 'error');
          if (partial) this.$root.$emit('modal-close', 'mark-as-paid-modal');
        }
      } finally {
        this.isProcessingPayment = false;
        this.$loader.end('processPayment');
      }
    },

    openMarkAsPaidModal() {
      this.$v.markAsPaid.$reset();
      this.markAsPaid = {
        comment: '',
        transactionId: '',
        gatewayType: '',
        typeOfPayment: 1
      };
      this.$root.$emit('modal-open', 'mark-as-paid-modal');
    },

    openCommissionAdvanceModal() {
      this.commissionAdvance = {
        amount: 0,
        comment: '',
        transactionId: ''
      };

      this.$root.$emit('modal-open', 'add-commission-advance-modal');
    },

    openClawbackModal() {
      this.clawback = {
        amount: 0,
        comment: '',
        transactionId: ''
      };

      this.$root.$emit('modal-open', 'add-clawback-modal');
    },

    openCommissionBonusModal() {
      this.bonus = {
        amount: 0,
        comment: '',
        transactionId: ''
      };

      this.$root.$emit('modal-open', 'add-commission-bonus-modal');
    },

    openFixNegativeBalanceModal() {
      // Check if current funnel has negative balance
      if (this.selectedAffiliateFunnel && this.affiliateFunnels.length > 0) {
        const currentFunnel = this.affiliateFunnels.find(f => f.id === this.selectedAffiliateFunnel);
        if (currentFunnel && currentFunnel.processed_without_advances < 0) {
          this.selectedNegativeFunnel = currentFunnel;
        } else {
          this.selectedNegativeFunnel = null;
        }
      } else {
        this.selectedNegativeFunnel = null;
      }
      this.$root.$emit('modal-open', 'fix-negative-balance-modal');
    },

    async refreshAffiliate() {
      if (this.selectedAffiliate) {
        this.$loader.start('refreshAffiliate');
        const affiliateId = this.selectedAffiliate.id;
        try {
          this.selectedAffiliate = await Affiliate.params({
            funnels: this.selectedAffiliateFunnel || this.selectedFunnel
          }).$find(affiliateId);
          this.applyFilters();
        } catch (error) {
          Notify.fail('Could not refresh affiliate balances. Please reload the page.');
        } finally {
          this.$loader.end('refreshAffiliate');
        }
      }
    },

    async addCommissionAdvance() {
      this.$v.commissionAdvance.$touch();
      if (this.$v.commissionAdvance.$invalid || this.isProcessingAdvance) return;

      this.isProcessingAdvance = true;
      const dialog = Swal.mixin({ target: this.$refs.commissionAdvanceModal.$el, keydownListenerCapture: true });
      let recorded = false;
      try {
        const sendAffiliateEmail = await chooseAffiliatePaymentEmail(dialog, this.$store.state.user);
        this.$loader.start('addCommissionAdvance');
        const funnelToUse = this.selectedAffiliateFunnel || this.selectedFunnel;
        const affiliatePayment = new AffiliatePayment({
          affiliate_id: this.selectedAffiliate.id,
          funnel: funnelToUse,
          amount: this.commissionAdvance.amount,
          payment_gateway: 0,
          note: this.commissionAdvance.comment,
          transaction_id: this.commissionAdvance.transactionId,
          type: 2,
          ...(sendAffiliateEmail === undefined ? {} : { send_affiliate_email: sendAffiliateEmail })
        });

        await affiliatePayment.save();
        recorded = true;
        await dialog.fire({ title: 'Commission advance recorded', text: 'The advance was saved. Returning to affiliate details.', icon: 'success', confirmButtonText: 'Done', allowOutsideClick: false });
        this.$root.$emit('modal-close', 'add-commission-advance-modal');
        this.selectedAffiliate = await Affiliate.params({ funnels: funnelToUse }).$find(this.selectedAffiliate.id);
        this.applyFilters();
      } catch (error) {
        const partial = error?.response?.data?.payment_recorded === true;
        const message = error?.response?.data?.message || 'Unable to confirm the advance. Check payment history before trying again.';
        if (recorded) {
          await Swal.fire('Advance recorded; refresh failed', 'The advance was saved. Reload affiliate details; do not add it again.', 'warning');
        } else {
          await dialog.fire(partial ? 'Advance saved; processing incomplete' : 'Commission advance could not be confirmed', message, partial ? 'warning' : 'error');
          if (partial) this.$root.$emit('modal-close', 'add-commission-advance-modal');
        }
      } finally {
        this.isProcessingAdvance = false;
        this.$loader.end('addCommissionAdvance');
      }
    },

    async addClawback() {
      this.$v.clawback.$touch();
      const isValid = !this.$v.clawback.$invalid;

      if (isValid) {
        this.$loader.start('addClawback');
        const self = this;
        const affiliatePayment = new AffiliatePayment({
          affiliate_id: self.selectedAffiliate.id,
          funnel: this.selectedAffiliateFunnel || this.selectedFunnel,
          amount: self.clawback.amount,
          payment_gateway: 0,
          note: self.clawback.comment,
          transaction_id: self.clawback.transactionId,
          type: 3
        });

        await affiliatePayment.save();
        this.$root.$emit('modal-close', 'add-clawback-modal');
        this.selectedAffiliate = await Affiliate.params({
          funnels: this.selectedAffiliateFunnel || this.selectedFunnel
        }).$find(self.selectedAffiliate.id);
        this.$loader.end('addClawback');
        this.applyFilters();
      }
    },

    async addAffiliatePaymentBonus() {
      this.$v.bonus.$touch();
      const isValid = !this.$v.bonus.$invalid;

      if (isValid) {
        this.$loader.start('addAffiliatePaymentBonus');
        const self = this;
        const affiliatePaymentBonus = new AffiliatePaymentBonus({
          affiliate_id: self.selectedAffiliate.id,
          funnel: this.selectedAffiliateFunnel || this.selectedFunnel,
          amount: self.bonus.amount,
          payment_gateway: 0,
          note: self.bonus.comment,
          transaction_id: self.bonus.transactionId
        });

        await affiliatePaymentBonus.save();
        this.$root.$emit('modal-close', 'add-commission-bonus-modal');
        this.selectedAffiliate = await Affiliate.params({
          funnels: this.selectedAffiliateFunnel || this.selectedFunnel
        }).$find(self.selectedAffiliate.id);
        this.$loader.end('addAffiliatePaymentBonus');
        this.applyFilters();
      }
    },

    onModelUpdated() {
      if (this.isAllSelected) {
        const self = this;
        this.gridApi.forEachNode(rowNode => {
          if (!self.unselectedAffiliateIds.includes(rowNode.data.id)) {
            rowNode.setSelected(true);
          }
        });
      }
    },

    onSelectionChanged(event) {
      const selectedNodes = event.api.getSelectedNodes();
      this.selectedRowLength = selectedNodes.length;
    },

    async exportSelectedAffiliates() {
      const self = this;
      self.$loader.start('exportSelectedAffiliates');
      const selectedNodes = self.gridApi.getSelectedNodes();
      const selectedAffiliateIds = [];

      for (let i = 0; i < selectedNodes.length; i++) {
        selectedAffiliateIds.push(selectedNodes[i].data.id);
      }

      const exportLinkData = await axios.post(
        '/generate-affiliates-export-link',
        {
          selected: selectedAffiliateIds,
          unselected: self.unselectedAffiliateIds,
          isAllSelected: self.isAllSelected
        }
      );

      const a = document.createElement('a');
      a.href = exportLinkData.data.download_link.replace(
        /^http:\/\//i,
        'https://'
      );
      a.click();

      self.$loader.end('exportSelectedAffiliates');
    },

    onRowSelected(event) {
      if (
        !event.node.selected &&
        this.unselectedAffiliateIds.indexOf(event.node.data.id) === -1
      ) {
        this.unselectedAffiliateIds.push(event.node.data.id);
      } else {
        this.unselectedAffiliateIds = this.unselectedAffiliateIds.filter(
          item => item !== event.node.data.id
        );
      }
    },

    selectAllAffiliates() {
      this.isAllSelected = true;
      this.gridApi.forEachNode(node => {
        node.setSelected(true);
      });

      this.unselectedAffiliateIds = [];
    },

    unselectAllAffiliates() {
      this.isAllSelected = false;
      this.gridApi.forEachNode(node => {
        node.setSelected(false);
      });

      this.unselectedAffiliateIds = [];
    },

    selectFunnel(funnel) {
      this.openAffiliateDetail(
        this.selectedAffiliateForFunnelSelector,
        funnel.id
      );
    },

    async openAffiliateDetail(affiliateId, funnel_id = null) {
      let funnel = this.selectedFunnel;

      if (this.selectedFunnel == 0 && funnel_id > 0) {
        funnel = funnel_id;
      }

      if (funnel == 0) {
        this.selectedAffiliateForFunnelSelector = affiliateId;
        this.$loader.start('openAffiliateDetailFunnelSelector');
        const { data } = await axios.get(`affiliates/${affiliateId}/funnels`);
        this.affiliateFunnels = data.data;
        this.$root.$emit('modal-open', 'affiliate-funnel-selector-modal');

        this.$loader.end('openAffiliateDetailFunnelSelector');
        return;
      }

      this.$loader.start('openAffiliateDetail');
      this.selectedAffiliateFunnel = funnel; // Store the actual funnel used
      this.selectedAffiliate = await Affiliate.params({
        funnels: funnel
      }).$find(affiliateId);
      this.affiliateDetailsTab = 'info';
      this.$root.$emit('modal-open', 'affiliate-detail-modal');
      this.$loader.end('openAffiliateDetail');
      // console.log(transactionId);
    },


    onAffiliateSortChanged() {
      this.affiliateRequestGeneration += 1;
    },

    applyFilters(isClearFilters = false) {
      clearTimeout(this.searchTimer);
      if (!this.gridApi) return;
      this.affiliateRequestGeneration += 1;
      this.affiliateLoadError = false;
      if (!isClearFilters) {
        this.applyTriggered = true;
      }

      this.gridApi.paginationGoToPage(0);
      this.gridApi.gridOptionsWrapper.setProperty(
        'cacheBlockSize',
        this.selectedRowSize
      );
      this.gridApi.infiniteRowModel.resetCache();
      this.gridApi.paginationSetPageSize(Number(this.selectedRowSize));
    },

    async onGridReady() {
      this.gridApi = this.gridOptions.api;
      this.gridColumnApi = this.gridOptions.columnApi;
      this.dataSource = await this.getDataSource(Number(this.selectedRowSize));
    },

    onFirstDataRendered(params) {
      params.api.sizeColumnsToFit();
    },

    getDataSource() {
      const self = this;
      function MyDatasource(rowCount) {
        this.rowCount = rowCount;
      }

      MyDatasource.prototype.getRows = async function(params) {
        self.$loader.start('getDataSource');

        const generation = self.affiliateRequestGeneration;
        const limit = params.endRow - params.startRow;
        const search = self.searchPhrase.trim();
        try {
          const affiliatesData = await Affiliate.page(Math.floor(params.startRow / limit) + 1)
            .limit(limit)
            .params({
              affiliates: self.filteredAffiliateTypes,
              search,
              funnels: self.selectedFunnel,
              sortBy: params.sortModel,
              noOfSales: self.filteredNumberOfSales,
              salesAmount: self.filteredSalesAmount,
              commissionsAmount: self.filteredCommissionsAmount,
              dueCommissionsType: self.filteredDueCommissionsType,
              dueAmount: self.filteredDueAmount
            })
            .get();
          if (generation !== self.affiliateRequestGeneration || search !== self.searchPhrase.trim()) {
            params.failCallback();
            return;
          }
          self.affiliateLoadError = false;
          self.funnels = affiliatesData.meta.funnels;
          params.successCallback(affiliatesData.data, affiliatesData.meta.total);
        } catch (error) {
          if (generation !== self.affiliateRequestGeneration || search !== self.searchPhrase.trim()) {
            params.failCallback();
            return;
          }
          self.affiliateLoadError = true;
          params.failCallback();
        } finally {
          self.$loader.end('getDataSource');
        }
      };

      return new MyDatasource(Number(self.selectedRowSize));
    }
  }
};
</script>
<style>
.dropdown-toggle {
  background-image: none !important;
  border: 1px solid dodgerblue !important;
  border-radius: 30px;
}

.dropdown-toggle:hover {
  background: transparent;
}

.daterangepicker .ranges li.active {
  background-color: var(--brand-primary);
}

.daterangepicker td.active,
.daterangepicker td.active:hover {
  background-color: var(--brand-primary);
}
</style>

<style scoped>
.gs-affiliates-page {
  min-height: 100%;
  padding: 28px;
  color: var(--gs-foreground);
  background: var(--gs-background);
}

.gs-affiliates-page__content {
  max-width: 1440px;
  margin: 0 auto;
}

.gs-affiliates-page__filters {
  display: grid;
  padding: 14px 16px;
  gap: 12px;
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-lg);
  background: var(--gs-surface);
}
.gs-affiliates-page__filter-row {
  display: grid;
  grid-template-columns: minmax(160px, 0.8fr) minmax(180px, 1fr) minmax(240px, 1.5fr) 86px;
  align-items: center;
  gap: 10px;
}
.gs-affiliates-page__filter-row > div { min-width: 0; }
.gs-affiliates-page__filter-row .gs-name-select { width: 100%; min-width: 0; }
.gs-affiliates-page__toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
}
.gs-affiliates-page__filter-actions,
.gs-affiliate-action-row { display: flex; align-items: center; flex-wrap: wrap; gap: 8px; }
.gs-affiliate-action-row > .gs-name-select { width: 180px; min-width: 0; }
.gs-affiliates-page__hint { margin: 8px 2px 0; font-size: 12px; color: var(--gs-muted-foreground); }
.gs-affiliates-page__error { padding: 12px; color: #9f1239; }
.gs-affiliates-page__error button { text-decoration: underline; }


.gs-affiliates-page__table {
  min-width: 0;
  overflow-x: auto;
  background: var(--gs-surface);
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-lg);
  box-shadow: var(--gs-shadow-sm);
}

@media (max-width: 960px) {
  .gs-affiliates-page__filter-row { grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) 86px; }
  .gs-affiliates-page__search { grid-column: 1 / -1; grid-row: 2; }
  .gs-affiliates-page__page-size { grid-column: 3; grid-row: 1; }
}

@media (max-width: 640px) {
  .gs-affiliates-page {
    padding: 16px;
  }

  .gs-affiliates-page__filters {
    padding: 12px;
    min-width: 0;
  }
  .gs-affiliates-page__filter-row { grid-template-columns: minmax(0, 1fr) 86px; }
  .gs-affiliates-page__page-size { grid-column: 2; }
  .gs-affiliates-page__funnel { grid-column: 1 / -1; grid-row: 2; }
  .gs-affiliates-page__search { grid-row: 3; }
  .gs-affiliates-page__filter-actions,
  .gs-affiliate-action-row { flex: 1 1 100%; }
  .gs-affiliates-page__filter-actions > * { flex: 1 1 auto; }
  .gs-affiliate-action-row > .gs-name-select { flex: 1 1 170px; width: auto; min-width: 0; }
}
</style>
<style lang="scss">
.tooltip {
  display: block !important;
  z-index: 10000;

  .tooltip-inner {
    background: black;
    color: white;
    border-radius: 16px;
    padding: 5px 10px 4px;
  }

  .tooltip-arrow {
    width: 0;
    height: 0;
    border-style: solid;
    position: absolute;
    margin: 5px;
    border-color: black;
    z-index: 1;
  }

  &[x-placement^='top'] {
    margin-bottom: 5px;

    .tooltip-arrow {
      border-width: 5px 5px 0 5px;
      border-left-color: transparent !important;
      border-right-color: transparent !important;
      border-bottom-color: transparent !important;
      bottom: -5px;
      left: calc(50% - 5px);
      margin-top: 0;
      margin-bottom: 0;
    }
  }

  &[x-placement^='bottom'] {
    margin-top: 5px;

    .tooltip-arrow {
      border-width: 0 5px 5px 5px;
      border-left-color: transparent !important;
      border-right-color: transparent !important;
      border-top-color: transparent !important;
      top: -5px;
      left: calc(50% - 5px);
      margin-top: 0;
      margin-bottom: 0;
    }
  }

  &[x-placement^='right'] {
    margin-left: 5px;

    .tooltip-arrow {
      border-width: 5px 5px 5px 0;
      border-left-color: transparent !important;
      border-top-color: transparent !important;
      border-bottom-color: transparent !important;
      left: -5px;
      top: calc(50% - 5px);
      margin-left: 0;
      margin-right: 0;
    }
  }

  &[x-placement^='left'] {
    margin-right: 5px;

    .tooltip-arrow {
      border-width: 5px 0 5px 5px;
      border-top-color: transparent !important;
      border-right-color: transparent !important;
      border-bottom-color: transparent !important;
      right: -5px;
      top: calc(50% - 5px);
      margin-left: 0;
      margin-right: 0;
    }
  }

  &.popover {
    $color: #f9f9f9;

    .popover-inner {
      background: $color;
      color: black;
      padding: 24px;
      border-radius: 5px;
      box-shadow: 0 5px 30px rgba(black, 0.1);
    }

    .popover-arrow {
      border-color: $color;
    }
  }

  &[aria-hidden='true'] {
    visibility: hidden;
    opacity: 0;
    transition: opacity 0.15s, visibility 0.15s;
  }

  &[aria-hidden='false'] {
    visibility: visible;
    opacity: 1;
    transition: opacity 0.15s;
  }
}
</style>

<style lang="scss">
#affiliate-transactions-list-view,
#affiliate-refunds-list-view,
#affiliate-payments-list-view,
#second-tier-affiliates-list-view,
#affiliate-links-list-view {
  .VueTables--client {
    width: 100%;
  }

  .VueTables--client table {
    overflow: auto !important;
  }

  .VueTables__search__input {
    border: 1px solid #eee;
    border-radius: 5px;
    margin-right: 20px;
    font-size: 14px;
    padding: 6px !important;
  }

  .VueTables__search-field {
    margin-bottom: 10px;
    display: flex;
    justify-content: flex-end;
  }

  .VueTables__heading {
    color: #8f92ab;
  }

  .VueTables__sort-icon {
    color: #8f92ab;
    margin-left: 8px;
  }

  td {
    font-weight: 400;
    outline: 2px solid transparent;
    outline-offset: 2px;
  }

  .VueTables__child-row-toggler--closed::before {
    content: url("data:image/svg+xml; utf8, <svg width='20' height='20' xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20'><path d='m3.707031 7l-.707031.707031 6.125 6.125 1.875 1.875 1.875-1.875 6.125-6.125-.707031-.707031-6.125 6.125-1.167969 1.167969-1.167969-1.167969-6.125-6.125' /></svg>");
    padding-right: 3px;
    vertical-align: middle;
  }

  .VueTables__child-row {
    text-align: center;
    border-bottom: 1px solid #ddd;
    border-left: 5px solid var(--brand-primary);
  }

  .VueTables__child-row-toggler--open::before {
    content: url("data:image/svg+xml; utf8, <svg width='20' height='20' xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20'><path d='m3.707031 1044.3622l-.707031-.707 6.125-6.125 1.875001-1.875 1.875 1.875 6.125 6.125-.707032.707-6.125-6.125-1.167968-1.168-1.167969 1.168-6.125 6.125' transform='translate(0-1030.3622)'/></svg>");
    padding-right: 3px;
    vertical-align: middle;
  }

  .VueTables__table {
    border-collapse: collapse;
    border-spacing: 0;
    width: 100%;
  }

  .VueTables__row {
    border-bottom: 1px solid #ddd;
    font-size: 14px;
  }

  .VueTables__table th,
  td {
    text-align: left;
    padding: 10px;
  }

  tr.VueTables__child-row > td {
    padding: 0px !important;
  }

  thead {
    border-bottom: 1px solid #ddd;
  }

  .vue-pagination-ad {
    text-align: center;
  }

  .VueTables__limit {
    display: none;
  }

  .VuePagination__count {
    display: none;
  }

  .expandIcon {
    display: none;
  }

  .VuePagination__pagination {
    display: flex;
    margin-top: 10px;
    justify-content: flex-end;
    margin-right: 10px;
    margin-bottom: 10px;
  }

  .VuePagination__pagination-item {
    padding-left: 2px;
    padding-right: 2px;
  }
}
</style>

<style lang="scss">
.dropdown-button-container {
  display: flex;
  align-items: center;

  .btnx {
    border-radius: 5px 0px 0px 5px;
  }

  .btn-drop {
    border-radius: 0px 5px 5px 0px;
    border-left: 1px solid rgba(255, 255, 255, 0.2);
  }
}
</style>

<style lang="scss">
#affiliates-list-view {
  // Shared table themes load after page styles. Keep this page's neutral header.
  .ag-theme-material .ag-header-group-cell.affiliate-details-header {
    background: #f8fafc !important;
    color: #475569 !important;
  }
  .ag-theme-material .affiliate-details-header .ag-header-group-text {
    color: #475569 !important;
  }
  .ag-header { background: #f8fafc; border-bottom: 1px solid #e2e8f0; }
  .ag-row { border-color: #edf0f4; }
  .ag-row-odd { background: #fafbfd; }
  .ag-row-hover { background: #fff2f7; }
  .ag-cell { padding-left: 14px; padding-right: 14px; }
  .ag-paging-panel { border-top: 1px solid #e2e8f0; }

  .ag-unselectable {
    -webkit-user-select: text !important;
    user-select: initial !important;
  }

  .affiliate-details-header {
    background-color: #f8fafc;
    text-align: center !important;
    color: #475569;
    border-right: 1px solid #e2e8f0;
    border-top: 3px solid var(--brand-primary);
  }

  .affiliate-details-header .ag-header-group-cell-label {
    text-align: center;
  }

  .affiliate-details-header
    .ag-header-group-cell-label
    span.ag-header-group-text {
    margin: auto;
  }

  .VueTables__search-field {
    margin-bottom: 10px;
  }

  .VueTables__heading {
    color: #8f92ab;
  }

  .VueTables__sort-icon {
    color: #8f92ab;
    margin-left: 8px;
  }

  td {
    font-weight: 400;
    outline: 2px solid transparent;
    outline-offset: 2px;
  }

  .VueTables__child-row-toggler--closed::before {
    content: url("data:image/svg+xml; utf8, <svg width='20' height='20' xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20'><path d='m3.707031 7l-.707031.707031 6.125 6.125 1.875 1.875 1.875-1.875 6.125-6.125-.707031-.707031-6.125 6.125-1.167969 1.167969-1.167969-1.167969-6.125-6.125' /></svg>");
    padding-right: 3px;
    vertical-align: middle;
  }

  .VueTables__child-row {
    text-align: center;
    border-bottom: 1px solid #ddd;
    border-left: 5px solid var(--brand-primary);
  }

  .VueTables__child-row-toggler--open::before {
    content: url("data:image/svg+xml; utf8, <svg width='20' height='20' xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20'><path d='m3.707031 1044.3622l-.707031-.707 6.125-6.125 1.875001-1.875 1.875 1.875 6.125 6.125-.707032.707-6.125-6.125-1.167968-1.168-1.167969 1.168-6.125 6.125' transform='translate(0-1030.3622)'/></svg>");
    padding-right: 3px;
    vertical-align: middle;
  }

  .VueTables__table {
    border-collapse: collapse;
    border-spacing: 0;
    width: 100%;
  }

  .VueTables__row {
    border-bottom: 1px solid #ddd;
  }

  .VueTables__table th,
  td {
    text-align: left;
    padding: 16px;
  }

  tr.VueTables__child-row > td {
    padding: 0px !important;
  }

  thead {
    border-bottom: 1px solid #ddd;
  }

  .vue-pagination-ad {
    text-align: center;
  }

  .VueTables__limit {
    display: none;
  }

  .VuePagination__count {
    display: none;
  }
}
</style>

<style>
.daterangepicker.show-ranges .drp-calendar.left {
  border: none !important;
}

.daterangepicker.show-calendar .ranges {
  border-right: 1px solid #ddd;
}
</style>

<style>
.multiselect__placeholder {
  font-size: 16px;
}

.multiselect__option--highlight {
  background-color: var(--brand-primary) !important;
}

.multiselect__tag-icon:focus,
.multiselect__tag-icon:hover {
  background-color: var(--brand-primary) !important;
}

.multiselect__option--highlight:after {
  background-color: var(--brand-primary) !important;
}

.multiselect__option--selected.multiselect__option--highlight {
  background-color: #ff765b !important;
}

.multiselect__option--selected.multiselect__option--highlight:after {
  background-color: #ff765b !important;
}
</style>

<style>
.gs-default-tabs-item:focus {
  outline: none !important;
  outline-style: none !important;
}

.ag-icon-checkbox-checked {
  color: var(--brand-primary) !important;
}
</style>
